import { Card, Col, Empty, Row, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "../MarkdownLink";

export interface SpotlightProject {
  id: string;
  name?: string;
  impactHeadline?: string;
  impactStory?: string;
  impactMetrics?: { value: string; label: string }[];
  impactImageUrl?: string;
  impactLastReviewedAt?: string;
}

interface ImpactSpotlightCardProps {
  projects: SpotlightProject[];
}

const PRIMARY = "#092940";
// Explicit AA-passing muted tone (≥5.4:1 on the card's light gradient); Ant's
// default `type="secondary"` (~3.5:1) would fail WCAG 2.1 AA for small text.
const MUTED = "#56657a";

/** ISO timestamp → "Jun 2, 2026"; empty string when missing/unparseable. */
function formatImpactDate(iso?: string): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  return new Date(ts).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ProjectImpactCard({ project }: { project: SpotlightProject }) {
  const navigate = useNavigate();
  const open = () => navigate(`/projects?id=${encodeURIComponent(project.id)}`);
  return (
    <Card
      hoverable
      onClick={open}
      role="link"
      aria-label={`Open project ${project.name || "(untitled)"}`}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
      style={{
        height: "100%",
        background: "linear-gradient(180deg, #EEF7FF 0%, #FFFFFF 70%)",
        borderColor: "rgba(9,41,64,0.08)",
      }}
      bodyStyle={{ padding: 16 }}
    >
      <Typography.Text style={{ color: PRIMARY, fontWeight: 600, fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {project.name || "Project"}
      </Typography.Text>
      <Typography.Title level={5} style={{ margin: "4px 0 8px 0", color: PRIMARY }}>
        {project.impactHeadline || "Outcome captured"}
      </Typography.Title>
      {project.impactMetrics && project.impactMetrics.length > 0 && (
        <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
          {project.impactMetrics.slice(0, 2).map((metric, idx) => (
            <Col key={idx} span={12}>
              <div
                style={{
                  borderRadius: 8,
                  background: "#fff",
                  border: "1px solid rgba(9,41,64,0.08)",
                  padding: "8px 10px",
                }}
              >
                <div style={{ fontSize: 22, fontWeight: 700, color: PRIMARY, lineHeight: 1.1 }}>
                  {metric.value}
                </div>
                <div style={{ fontSize: 12, color: "#56657a", marginTop: 2 }}>
                  {metric.label}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
      {project.impactStory && (
        <div style={{ fontSize: 13, color: "#283744" }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {project.impactStory.length > 320
              ? `${project.impactStory.slice(0, 320).trim()}…`
              : project.impactStory}
          </ReactMarkdown>
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginTop: 8,
        }}
      >
        {project.impactLastReviewedAt ? (
          <Typography.Text style={{ fontSize: 12, color: MUTED }}>
            As of {formatImpactDate(project.impactLastReviewedAt)}
          </Typography.Text>
        ) : (
          <span />
        )}
        <Typography.Link style={{ fontSize: 13 }}>View project →</Typography.Link>
      </div>
    </Card>
  );
}

export default function ImpactSpotlightCard({ projects }: ImpactSpotlightCardProps) {
  return (
    <Card title="Impact spotlight" extra={<Typography.Text type="secondary" style={{ fontSize: 13 }}>Outcomes from real projects</Typography.Text>}>
      {projects.length === 0 ? (
        <Empty description="Once a project lands outcomes worth showing leadership, flip its Spotlight toggle in Project Details → Impact Spotlight." />
      ) : (
        <Row gutter={[12, 12]}>
          {projects.slice(0, 3).map((project) => (
            <Col xs={24} md={8} key={project.id}>
              <ProjectImpactCard project={project} />
            </Col>
          ))}
        </Row>
      )}
    </Card>
  );
}
